import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Bag2, Box1, DollarCircle, TickCircle } from "iconsax-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { GenericStatCard } from "@/components/Molecules";
import { formatPersianNumber } from "@/lib/format/persian";
import type { OrderStatsRowProps } from "./OrderStatsRow.types";
import * as styles from "./OrderStatsRow.styles";

export type { OrderStatsRowProps } from "./OrderStatsRow.types";

export const OrderStatsRow = memo(function OrderStatsRow({
  stats,
  isLoading = false,
}: OrderStatsRowProps) {
  const { t } = useTranslation("orders");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const iconSize = isMobile ? 20 : 25;

  const items = [
    {
      value: formatPersianNumber(stats?.total_count ?? 0),
      label: t("stats.total"),
      icon: <Bag2 size={iconSize} variant="Bold" color={theme.palette.primary.main} />,
      accent: theme.palette.primary.main,
    },
    {
      value: formatPersianNumber(stats?.pending_payment_count ?? 0),
      label: t("stats.pendingPayment"),
      icon: <DollarCircle size={iconSize} variant="Bold" color={theme.palette.warning.main} />,
      accent: theme.palette.warning.main,
      valueColor: theme.palette.warning.main,
    },
    {
      value: formatPersianNumber(stats?.preparing_count ?? 0),
      label: t("stats.preparing"),
      icon: <Box1 size={iconSize} variant="Bold" color={theme.palette.secondary.main} />,
      accent: theme.palette.secondary.main,
    },
    {
      value: formatPersianNumber(stats?.completed_count ?? 0),
      label: t("stats.completed"),
      icon: <TickCircle size={iconSize} variant="Bold" color={theme.palette.success.main} />,
      accent: theme.palette.success.main,
    },
  ];

  return (
    <Box sx={styles.grid}>
      {items.map((item) => (
        <GenericStatCard
          key={item.label}
          value={item.value}
          label={item.label}
          icon={item.icon}
          accentColor={item.accent}
          valueColor={"valueColor" in item ? item.valueColor : undefined}
          isLoading={isLoading}
        />
      ))}
    </Box>
  );
});
